import cn from 'classnames';
import React from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';

import { SPECIAL_FOLDER_ID_ARCHIVED } from 'constants/folders';
import { FAVORITES_PAGE } from 'constants/routes';
import { FOLDER, VIDEO } from 'constants/videosDashboard';
import { SHARED_WORKSPACE } from 'constants/workspaces';
import {
  archiveRestoreFolderCreator,
  bulkAddFavoritesCreator,
  bulkMoveFoldersVideosCreator,
  moveFolderCreator,
  moveVideoCreator,
  toggleFolderFavoriteCreator,
  toggleVideoFavoriteCreator,
} from 'creators/videosDashboard';
import * as sentry from 'utilities/sentry';

import styles from './DropItem.module.less';

export function DropItem({ children, item }) {
  const dispatch = useDispatch();
  const {
    checked_items: checkedItems,
    dragging_multiple: draggingMultiple,
    folder_id: currentFolderId,
    is_archived: isArchived,
    workspace,
    videos,
    dragging_items: draggingItems,
  } = useSelector(state => state.videos_dashboard);
  const [props, drop] = useDrop({
    accept: [VIDEO, FOLDER],
    drop(_, monitor) {
      try {
        if (monitor.didDrop()) {
          return;
        }

        const itemId = monitor.getItem().id;
        const itemType = monitor.getItemType();
        const folderId = item.id;

        const draggingMultipleCheckedItems =
          checkedItems.length > 1 && draggingMultiple;
        const archived = folderId === SPECIAL_FOLDER_ID_ARCHIVED;
        const isFolder = itemType === FOLDER;
        const isVideo = itemType === VIDEO;
        const starred = item.path === FAVORITES_PAGE;

        if (draggingMultipleCheckedItems && archived) {
          dispatch(bulkMoveFoldersVideosCreator(true, folderId));
        } else if (draggingMultipleCheckedItems && isArchived) {
          dispatch(bulkMoveFoldersVideosCreator(false, folderId));
        } else if (draggingMultipleCheckedItems && starred) {
          dispatch(bulkAddFavoritesCreator());
        } else if (draggingMultipleCheckedItems) {
          dispatch(
            bulkMoveFoldersVideosCreator(null, folderId, null, item.workspace)
          );
        } else if (isFolder && isArchived) {
          dispatch(archiveRestoreFolderCreator(itemId, false, folderId));
        } else if (isFolder && archived) {
          dispatch(archiveRestoreFolderCreator(itemId, true));
        } else if (isFolder && starred) {
          dispatch(toggleFolderFavoriteCreator(itemId, true));
        } else if (isFolder) {
          dispatch(moveFolderCreator(itemId, folderId, item.workspace));
        } else if (isVideo && isArchived) {
          dispatch(moveVideoCreator(itemId, folderId, false));
        } else if (isVideo && archived) {
          dispatch(moveVideoCreator(itemId, folderId, true));
        } else if (isVideo && starred) {
          dispatch(toggleVideoFavoriteCreator(itemId, true));
        } else if (isVideo) {
          dispatch(moveVideoCreator(itemId, folderId, null, item.workspace));
        } else {
          throw new Error('attempted to move invalid item into favorite');
        }

        return { moved: true };
      } catch (err) {
        sentry.logException(err, 'error moving item in videos dashboard');

        // must return undefined for react-dnd to avoid the erroneous object
        // from becoming drop result
        return;
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    canDrop(_, monitor) {
      const draggedItemId = monitor.getItem().id;

      if (!item.withDrop) {
        return false;
      }

      const canDropArgs = {
        checkedItems,
        currentItemId: item.id,
        currentFolderId,
        draggedItemId,
        draggingMultiple,
        isArchived,
        targetWorkspace: item.workspace,
        workspace,
        videos,
      };

      return canDropItem(canDropArgs);
    },
  });

  const className = cn(styles.dropZone, {
    [styles.hover]: props.isOver && props.canDrop,
    [styles.disabled]: draggingItems && !props.canDrop,
  });

  const clone = React.cloneElement(React.Children.only(children), {
    ref: drop,
  });

  return <div className={className}>{clone}</div>;
}

function canDropItem(canDropArgs) {
  const {
    checkedItems,
    currentItemId,
    currentFolderId,
    draggingMultiple,
    draggedItemId,
    isArchived,
    targetWorkspace,
  } = canDropArgs;

  if (targetWorkspace === SHARED_WORKSPACE) {
    return false;
  }

  if (
    currentFolderId === currentItemId ||
    (isArchived &&
      (!currentItemId || currentItemId === SPECIAL_FOLDER_ID_ARCHIVED))
  ) {
    return false;
  }

  const isPartOfCheckedItems = _isPartOfCheckedItems(
    checkedItems,
    currentItemId
  );
  const itemIsNotItself = currentItemId !== draggedItemId;
  const partOfMultipleDrag = draggingMultiple && isPartOfCheckedItems;

  return itemIsNotItself && !partOfMultipleDrag;
}

function _isPartOfCheckedItems(checkedItems = [], currentItemId) {
  return checkedItems.some(
    ({ id, type }) => id === currentItemId && type === FOLDER
  );
}
